<template>
  <v-app-bar class="px-4">
    <v-img
      max-height="30"
      max-width="30"
      :src="headerLogoUrl"
      @click="handleLogoClick()"
    />
    <v-app-bar-title>{{ APP_NAME }}</v-app-bar-title>

    <dev-mode-chip />

    <v-tooltip location="bottom" text="About">
      <template #activator="{ props: aboutProps }">
        <v-btn
          v-bind="aboutProps"
          icon
          size="small"
          variant="text"
          @click="aboutDialog = true"
        >
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-tooltip location="bottom" :text="themeMeta.label">
      <template #activator="{ props: themeProps }">
        <v-btn
          v-bind="themeProps"
          icon
          size="small"
          variant="text"
          @click="toggleTheme"
        >
          <v-icon>{{ themeMeta.icon }}</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-tooltip location="bottom" :text="fullScreenMeta.label">
      <template #activator="{ props: fullScreenProps }">
        <v-btn
          v-bind="fullScreenProps"
          icon
          size="small"
          variant="text"
          @click="handleFullscreenClick"
        >
          <v-icon>{{ fullScreenMeta.icon }}</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-divider v-if="!showMenu" class="mx-2" vertical />

    <v-menu v-if="authStore.user" location="bottom">
      <template #activator="{ props: accountProps }">
        <v-btn v-bind="accountProps" class="text-none" variant="text">
          <v-avatar class="mr-2" size="28">
            <v-img
              v-if="avatarImageUrl"
              alt="User avatar"
              :src="avatarImageUrl"
            />
            <span v-else class="text-body-small font-weight-medium">
              {{ displayInitials }}
            </span>
          </v-avatar>
          {{ displayName }}
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-list density="compact" nav>
        <template v-for="(item, i) in compacted" :key="item.value ?? `i-${i}`">
          <v-divider
            v-if="item.divider || item.type === 'divider'"
            class="my-2"
            role="separator"
          />
          <nav-list-item v-else :item="item" @item-click="onMenuItemClick" />
        </template>
      </v-list>
    </v-menu>

    <AboutDialog v-model="aboutDialog" />
  </v-app-bar>
</template>

<script setup>
  import { useXappCoreAuthAdapter } from '#xapp-core/foundation'
  import { useXappCoreIdentityAdapter } from '#xapp-core/foundation'
  import { useDevMode } from '#xapp-core/foundation'
  import { usePermissionEvaluator } from '#xapp-core/foundation'
  import { APP_NAME } from '#xapp-core/foundation'
  import { setStoredTheme } from '#xapp-core/foundation'
  import { computed, ref } from 'vue'
  import { useTheme } from 'vuetify'

  import logoBlackUrl from '../../assets/x-logo-black.svg'
  import logoWhiteUrl from '../../assets/x-logo-white.svg'
  import AboutDialog from '../AboutDialog.vue'

  const props = defineProps({
    fullScreen: {
      default: null,
      type: Boolean,
    },
    menuItems: {
      default: () => [],
      type: Array,
    },
    onToggleFullScreen: {
      default: null,
      type: Function,
    },
    onToggleTheme: {
      default: null,
      type: Function,
    },
    // Whether to show the full menu (true) or just the made by text (false)
    showMenu: {
      default: true,
      type: Boolean,
    },
  })
  const emit = defineEmits(['menu-action', 'ui-error'])

  const theme = useTheme()
  const { enableDevMode } = useDevMode()
  const { hasPermission } = usePermissionEvaluator()

  const authStore = useXappCoreAuthAdapter()
  const identityAdapter = useXappCoreIdentityAdapter()
  const aboutDialog = ref(false)

  // ---------------------------------------------------------------------------
  // Full Screen Logic
  // ---------------------------------------------------------------------------

  const isFullScreen = ref(false)
  const effectiveIsFullScreen = computed(() =>
    typeof props.fullScreen === 'boolean'
      ? props.fullScreen
      : isFullScreen.value
  )

  // Prefer host-provided fullscreen orchestration (desktop/Electron); fallback to browser fullscreen for web-only usage.
  async function handleFullscreenClick() {
    if (typeof props.onToggleFullScreen === 'function') {
      const result = await props.onToggleFullScreen()
      if (result?.ok === false) {
        emit('ui-error', result.error?.message || 'Failed to toggle fullscreen')
      }
      return
    }

    if (document.fullscreenElement) {
      await document.exitFullscreen()
      isFullScreen.value = false
    } else {
      await document.documentElement.requestFullscreen()
      isFullScreen.value = true
    }
  }

  const fullScreenMeta = computed(() => ({
    icon: effectiveIsFullScreen.value
      ? 'mdi-fullscreen-exit'
      : 'mdi-fullscreen',
    label: effectiveIsFullScreen.value
      ? 'Exit Full Screen'
      : 'Enter Full Screen',
  }))

  // ---------------------------------------------------------------------------
  // Theme Logic
  // ---------------------------------------------------------------------------

  const themeMeta = computed(() => {
    const isDark = theme.global.current.value.dark
    return {
      icon: isDark ? 'mdi-weather-sunny' : 'mdi-weather-night',
      label: isDark ? 'Switch to Light Theme' : 'Switch to Dark Theme',
    }
  })

  const headerLogoUrl = computed(() => {
    const isDark = theme.global.current.value.dark
    return isDark ? logoWhiteUrl : logoBlackUrl
  })

  async function toggleTheme() {
    if (typeof props.onToggleTheme === 'function') {
      const result = await props.onToggleTheme()
      if (result?.ok === false) {
        emit('ui-error', result.error?.message || 'Failed to toggle theme')
      }
      return
    }

    const isDark = theme.global.current.value.dark
    const nextTheme = isDark ? 'light' : 'dark'
    theme.change(nextTheme)
    setStoredTheme(nextTheme)
  }

  // ---------------------------------------------------------------------------
  // Dev Mode Activation Logic
  // ---------------------------------------------------------------------------

  let clickCount = 0
  let clickTimer = null

  function handleLogoClick() {
    clickCount++

    if (!clickTimer) {
      clickTimer = setTimeout(() => {
        clickCount = 0
        clickTimer = null
      }, 4000)
    }

    if (clickCount === 8) {
      enableDevMode()
      clickCount = 0
      clearTimeout(clickTimer)
      clickTimer = null
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation Items
  // ---------------------------------------------------------------------------

  const navigationItems = computed(() => [
    ...(Array.isArray(props.menuItems) ? props.menuItems : []),
  ])

  const displayInitials = computed(() => {
    return identityAdapter.getInitials(authStore.user)
  })

  const displayName = computed(() => {
    return identityAdapter.getDisplayName(authStore.user)
  })

  const avatarImageUrl = computed(() => {
    const source = identityAdapter.getAvatarSource(authStore.user)
    return identityAdapter.resolveAvatarUrl(source)
  })

  function hasVisibleChildren(item) {
    const kids = Array.isArray(item.children) ? item.children : []
    return kids.some((element) => hasPermission(element))
  }

  function isVisible(item) {
    if (item.type === 'divider' || item.type === 'subheader') return true // handled by compactor
    if (Array.isArray(item.children) && item.children.length > 0) {
      return hasPermission(item) && hasVisibleChildren(item)
    }
    return hasPermission(item)
  }

  const compacted = computed(() => {
    const src = navigationItems.value
    const out = []
    let pendingHeader = null

    for (const item of src) {
      if (item.type === 'subheader') {
        // hold the header; only emit it if we later see a visible item
        pendingHeader = item
        continue
      }

      if (item.type === 'divider') {
        // close any pending header with no items (drop it)
        pendingHeader = null
        // avoid duplicate/trailing dividers
        if (out.at(-1)?.type !== 'divider') out.push(item)
        continue
      }

      // normal leaf or group
      if (!isVisible(item)) continue

      // if we have a pending header, emit it now (it has at least one visible item)
      if (pendingHeader) {
        out.push(pendingHeader)
        pendingHeader = null
      }

      out.push(item)
    }

    // drop trailing divider if present
    if (out.at(-1)?.type === 'divider') out.pop()

    return out
  })

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  function onMenuItemClick(node) {
    if (!node?.action) return
    emit('menu-action', node.action)
  }
</script>

<style scoped>
  .logout-color .v-list-item__prepend {
    color: red !important;
  }
  .v-list .v-divider[role='separator'] {
    margin-top: 8px;
    margin-bottom: 8px;
  }
  .link {
    text-decoration: none;
    color: inherit;
  }
  .link:hover {
    color: inherit;
  }
</style>
